/**
 * The one token syntax shared by everything that renders operator-authored
 * templates — notification messages and inventory labels. Keeping a single
 * implementation means `{{PRODUCT_NAME}}` behaves the same wherever it's typed,
 * rather than each surface growing its own dialect.
 */

/** Matches `{{{TOKEN}}}` first, then `{{TOKEN}}`. */
const TOKEN_RE = /\{\{\{([A-Za-z0-9_.]+)\}\}\}|\{\{([A-Za-z0-9_.]+)\}\}/g;

/**
 * Replaces every `{{{TOKEN}}}` / `{{TOKEN}}` in `content` with its value from
 * `data`.
 *
 * Triple-brace tokens are matched first so the double-brace alternative can't
 * consume the leading extra `{` and strand a `}`. Names are uppercased before
 * lookup, and dot-notation (`{{VENDOR_CONTACT.NAME}}`) is supported. A token
 * with no matching key resolves to an empty string — a half-rendered template is
 * worse than a gap, and operators shouldn't see raw braces on a printed label.
 * @param content - The template text
 * @param data - Token → value map; keys are matched uppercased
 * @param alias - Optional canonicaliser for legacy token names
 * @returns The rendered text
 */
export function replaceTokens(
  content: string,
  data: Record<string, string | number | undefined | null>,
  alias?: (uppercasedName: string) => string,
): string {
  return content.replace(
    TOKEN_RE,
    (_match, tripleToken: string, doubleToken: string) => {
      const uppercased = (tripleToken ?? doubleToken).toUpperCase();
      const canonical = alias ? alias(uppercased) : uppercased;
      const value = data[canonical];
      return value === undefined || value === null ? '' : String(value);
    },
  );
}

/**
 * The token names referenced by a template, uppercased and de-duplicated. Lets a
 * caller decide whether it needs to fetch the data behind a token group at all —
 * the label renderer skips its job/animal lookup entirely when no job token is
 * used.
 * @param content - The template text
 * @returns The distinct token names, uppercased
 */
export function tokensUsed(content: string): string[] {
  const found = new Set<string>();
  for (const m of content.matchAll(TOKEN_RE)) {
    found.add((m[1] ?? m[2]).toUpperCase());
  }
  return [...found];
}

/** True when `content` references at least one of `names` (case-insensitive). */
export function usesAnyToken(content: string, names: string[]): boolean {
  const wanted = new Set(names.map((n) => n.toUpperCase()));
  return tokensUsed(content).some((t) => wanted.has(t));
}
